import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  SxProps,
  Theme,
  Divider,
} from "@mui/material";

interface Year {
  id?: number;
  year?: number;
}

/** Sent when the user unchecks everything; matches no year (an empty list means "all years") */
export const NO_YEARS_SELECTED = -1;

const CHECK_ALL = "__checkAll__";
const UNCHECK_ALL = "__uncheckAll__";
const UNDER_CONTRACTS = "__underContracts__";

interface YearFilterSelectProps {
  years: Year[];
  selectedYearIds: number[];
  onChange: (yearIds: number[]) => void;
  label?: string;
  fullWidth?: boolean;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
  /** Pipeline folder id; when set, "Pipeline" is offered on top (unchecked by default) */
  pipelineYearId?: number;
  /** Name of the Pipeline folder option, e.g. "Active" in Analytics (only its Active entries count) */
  pipelineLabel?: string;
  /** Adds "Check all" / "Uncheck all" */
  bulkActions?: boolean;
  /** "Under Contracts" toggle (a flag, not a year); offered only when onUnderContractsChange is set */
  underContractsChecked?: boolean;
  onUnderContractsChange?: (checked: boolean) => void;
}

export const YearFilterSelect: React.FC<YearFilterSelectProps> = ({
  years,
  selectedYearIds,
  onChange,
  label = "Years",
  fullWidth = true,
  size = "small",
  sx,
  pipelineYearId,
  pipelineLabel = "Pipeline",
  bulkActions = false,
  underContractsChecked = false,
  onUnderContractsChange,
}) => {
  const allYearIds = years
    .map((y) => y.id)
    .filter((id): id is number => id !== undefined);

  // An empty selection means "all years" (never the Pipeline): show those as checked
  const checkedIds =
    bulkActions && selectedYearIds.length === 0
      ? allYearIds
      : selectedYearIds.filter((id) => id !== NO_YEARS_SELECTED);

  const handleChange = (e: SelectChangeEvent<(number | string)[]>) => {
    const value = e.target.value as (number | string)[];
    // Toggling "Under Contracts" never changes the selected years
    if (value.includes(UNDER_CONTRACTS)) {
      onUnderContractsChange?.(!underContractsChecked);
      return;
    }
    if (!bulkActions) {
      onChange(value as number[]);
      return;
    }
    if (value.includes(CHECK_ALL)) {
      onUnderContractsChange?.(true);
      onChange(
        pipelineYearId !== undefined
          ? [pipelineYearId, ...allYearIds]
          : [],
      );
      return;
    }
    if (value.includes(UNCHECK_ALL)) {
      onUnderContractsChange?.(false);
      onChange([NO_YEARS_SELECTED]);
      return;
    }
    const ids = value.filter((v): v is number => typeof v === "number");
    if (ids.length === 0) {
      onChange([NO_YEARS_SELECTED]);
    } else if (
      ids.length === allYearIds.length &&
      allYearIds.every((id) => ids.includes(id))
    ) {
      // Every year and no Pipeline: back to the canonical "all years"
      onChange([]);
    } else {
      onChange(ids);
    }
  };

  const yearName = (id: number) =>
    id === pipelineYearId
      ? pipelineLabel
      : years.find((y) => y.id === id)?.year?.toString();

  return (
    <FormControl fullWidth={fullWidth} size={size} sx={sx}>
      <InputLabel shrink={true}>{label}</InputLabel>
      <Select<(number | string)[]>
        multiple
        notched={true}
        value={checkedIds}
        displayEmpty
        onChange={handleChange}
        label={label}
        renderValue={() => {
          const underContracts =
            onUnderContractsChange && underContractsChecked
              ? " + Under Contracts"
              : "";
          if (selectedYearIds.length === 0) {
            return `All Years${underContracts}`;
          }
          if (checkedIds.length === 0) {
            return underContracts ? "Under Contracts" : "None selected";
          }

          if (checkedIds.length > 3) {
            return `${checkedIds.length} selected${underContracts}`;
          }

          return checkedIds.map(yearName).join(", ") + underContracts;
        }}
        MenuProps={{
          // The default "selectedMenu" focuses the last checked year, which
          // scrolls the menu to the bottom; open at the top instead
          variant: "menu",
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
      >
        {bulkActions && (
          <MenuItem value={CHECK_ALL} dense>
            <ListItemText primary="Check all" />
          </MenuItem>
        )}
        {bulkActions && (
          <MenuItem value={UNCHECK_ALL} dense>
            <ListItemText primary="Uncheck all" />
          </MenuItem>
        )}
        {bulkActions && <Divider />}
        {pipelineYearId !== undefined && (
          <MenuItem value={pipelineYearId}>
            <Checkbox checked={checkedIds.includes(pipelineYearId)} />
            <ListItemText primary={pipelineLabel} />
          </MenuItem>
        )}
        {onUnderContractsChange && (
          <MenuItem value={UNDER_CONTRACTS}>
            <Checkbox checked={underContractsChecked} />
            <ListItemText primary="Under Contracts" />
          </MenuItem>
        )}
        {(pipelineYearId !== undefined || onUnderContractsChange) && (
          <Divider />
        )}
        {years.map((year) => (
          <MenuItem key={year.id} value={year.id}>
            <Checkbox
              checked={year.id !== undefined && checkedIds.includes(year.id)}
            />
            <ListItemText primary={year.year} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
